import { Observer, ReplaySubject, mergeMap, take } from 'rxjs';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { LogLevel } from '../diagnostics/log-level';
import { Logging } from '../diagnostics/logging';
import { Strings } from '../generated/strings';
import { EnvironmentModule } from '../manifest/environment-modules';
import { AuthorizationManager } from '../security/authorization-manager';
import { GatewayConnection } from './gateway-connection';
import { headerConstants } from './http-constants';
import { Net } from './net';

/**
 * The state of Websocket connection.
 */
export enum WebsocketStreamConnectionState {
    /**
     * Initializing.
     */
    Initializing = 1,

    /**
     * Connected.
     */
    Connected,

    /**
     * Disconnected.
     */
    Disconnected,

    /**
     * Failed.
     */
    Failed,

    /**
     * Not configured.
     */
    NotConfigured
}

/**
 * The state of Websocket stream packet.
 */
export enum WebsocketStreamState {
    /**
     * Empty packet.
     */
    Noop = 1,

    /**
     * Data packet.
     */
    Data,

    /**
     * Error packet. (reserved for socket level error communication if any)
     */
    Error
}

/**
 * The request state of data such as CIM and PowerShell stream.
 */
export enum WebsocketStreamDataRequestState {
    /**
     * empty packet.
     */
    Noop = 1,

    /**
     * Data packet.
     */
    Normal,

    /**
     * Cancel
     */
    Cancel
}

/**
 * The response state of data such as CIM and PowerShell stream.
 */
export enum WebsocketStreamDataState {
    /**
     * empty packet.
     */
    Noop = 1,

    /**
     * Completed packet.
     */
    Completed,

    /**
     * Data packet.
     */
    Data,

    /**
     * Error
     */
    Error,

    /**
     * Cancelled
     */
    Cancelled
}

/**
 * Websocket Stream data target object.
 */
export interface WebsocketStreamDataTarget {
    /**
     * The node name.
     */
    nodeName: string;

    /**
     * The headers equivalent to REST API.
     */
    headers: MsftSme.StringMap<string>;
}

export enum WebsocketStreamName {
    CimStreamName = 'SME-CIM',
    PowerShellStreamName = 'SME-PowerShell',
    SshStreamName = 'SME-SSH',
    SystemStreamName = 'System'
};

/**
 * The request/response packet of Websocket Stream to the gateway.
 */
export interface WebsocketStreamPacket {
    /**
     * The identification string of protocol.
     *
     */
    streamName: WebsocketStreamName;

    /**
     * The state of packet.
     */
    state: WebsocketStreamState;

    /**
     * The request/response/error data.
     */
    data: any;

    /**
     * The options (reserved)
     */
    options?: any;
}

/**
 * Websocket stream handler.
 */
export interface WebsocketStreamHandler {
    /**
     * Call reset() when connection was lost to reset context.
     */
    reset(): void;

    /**
     * Call process() when message was received for the data type.
     */
    process(message: any): void;
}

/**
 * Websocket Stream Processor class.
 */
export class WebsocketStreamProcessor<TData, TOptions = void> {
    /**
     * Holding result if waitCompleted option is specified for multiple instances.
     */
    public response?: any;

    /**
     * Track closing state.
     */
    public closing?: boolean;

    /**
     * Track closed state.
     */
    public closed?: boolean;

    /**
     * Track observer end call by unsubscribe or observer completion.
     */
    public end?: boolean;

    /**
     * Sent once.
     */
    public sendOnce?: boolean;

    /**
     * Initializes a new instance of the CimProcessor class.
     * @param observer Observer to send back result to caller.
     * @param target Stream Target object.
     * @param options Options for Cim stream query.
     */
    constructor(public observer: Observer<TData>, public target: WebsocketStreamDataTarget, public options?: TOptions) {
    }

    /**
     * Push the result to the observer.
     * @param result the result of TData.
     */
    public next(result: TData): void {
        this.observer?.next(result);
        this.sendOnce = true;
    }

    /**
     * Complete the observer.
     */
    public complete(): void {
        this.closing = true;
        this.observer?.complete();

        this.closed = true;
    }

    /**
     * Error the observer.
     */
    public error(error: any): void {
        this.closing = true;
        this.observer?.error(error);
        this.closed = true;
    }
}

/**
 * The Websocket stream class.
 */
export class WebsocketStream {
    private static readonly logSourceName = 'WebsocketStream';
    private static maxConnectionRetries = 10;
    private static reconnectWaitTime = 500;
    public socketStateRaw: WebsocketStreamConnectionState = WebsocketStreamConnectionState.Disconnected;
    public socketState = new ReplaySubject<WebsocketStreamConnectionState>();
    private socket: WebSocketSubject<WebsocketStreamPacket | string>;
    private connectionRetries = WebsocketStream.maxConnectionRetries;
    private handlers = new Map<string, WebsocketStreamHandler>();
    private strings = MsftSme.getStrings<Strings>().MsftSmeShell.Core.WebsocketStream;

    /**
     * Initializes a new instance of the WebsocketStream class.
     *
     * @param gateway the gateway connection object.
     */
    constructor(public gateway: GatewayConnection) {
        // initialize only after gateway data was populated via RPC.
        this.gateway.initialize().pipe(
            mergeMap(() => this.gateway.navigationReadyObservable),
            take(1)
        ).subscribe(() => {
            // enable websocket stream only when the module added the options at initialization.
            const global = MsftSme.self();
            if (!global.Init.websocket && !global.Init.sshWebsocket) {
                this.socketState.next(WebsocketStreamConnectionState.NotConfigured);
                this.socketStateRaw = WebsocketStreamConnectionState.NotConfigured;
                return;
            }
            this.initialize(true);
        });
    }

    /**
     * Register the processor for the stream name.
     * @param name the name of stream.
     * @param handler the handler to process packet.
     */
    public registerProcessor(name: WebsocketStreamName, handler: WebsocketStreamHandler): void {
        this.handlers.set(name, handler);
    }

    /**
     * Send next stream data to websocket.
     *
     * @param streamName the stream name.
     * @param data the data to send.
     * @param options the options.
     */
    public sendNext(streamName: WebsocketStreamName, data: any, options?: any): void {
        if (!this.socket) {
            throw new Error('WebsocketStream: socket is not ready.');
        }

        const packet: WebsocketStreamPacket = { streamName, state: WebsocketStreamState.Data, data, options };
        this.debugLog('Socket sending data.', packet);
        this.socket.next(JSON.stringify(packet));
    }

    /**
     * Send error stream data to websocket.
     *
     * @param streamName the stream name.
     * @param error the error to send.
     * @param options the options.
     */
    public sendError(streamName: WebsocketStreamName, error: string, options?: any): void {
        if (!this.socket) {
            throw new Error('WebsocketStream: socket is not ready.');
        }

        const packet: WebsocketStreamPacket = { streamName, state: WebsocketStreamState.Error, data: error, options };
        this.debugLog('Socket sending error.', packet);
        this.socket.next(JSON.stringify(packet));
    }

    /**
     * Get target data.
     * @param authorizationManager the authorization manager.
     * @param nodeName the node Name
     * @param endpoint the endpoint data.
     * @return  WebsocketStreamDataTarget target data.
     */
    public getTarget(authorizationManager: AuthorizationManager, nodeName: string, endpoint?: string): WebsocketStreamDataTarget {
        const headers = authorizationManager.createTokenHeaders(nodeName);
        if (endpoint) {
            headers[headerConstants.POWERSHELL_ENDPOINT] = endpoint;
        }

        const target = <WebsocketStreamDataTarget>{ nodeName, headers };
        return target;
    }

    private initialize(firstTime: boolean): void {
        // get gateway socket url.
        const gatewaySocketUrl = this.gateway.gatewayUrl.replace('http', 'ws');
        const moduleName = MsftSme.self().Init.moduleName;
        let url = Net.streamSocket.format(gatewaySocketUrl, moduleName);
        if (EnvironmentModule.isGatewayV200) {
            url = Net.streamSocketV200.format(gatewaySocketUrl, moduleName);
        }
        const isSsh = MsftSme.self().Init.sshWebsocket;
        if (isSsh) {
            url = Net.sshStreamSocket.format(gatewaySocketUrl);
        }
        this.debugLog('Socket initializing...: {0}'.format(url));
        if (!firstTime) {
            this.handlers.forEach((value) => value.reset());
        }

        // create stream socket.
        this.socketState.next(WebsocketStreamConnectionState.Initializing);
        this.socket = webSocket<WebsocketStreamPacket | string>({
            url: url,
            openObserver: {
                next: () => {
                    this.debugLog('Socket opened: {0}'.format(url));
                    this.socketState.next(WebsocketStreamConnectionState.Connected);
                    this.socketStateRaw = WebsocketStreamConnectionState.Connected;
                    this.connectionRetries = WebsocketStream.maxConnectionRetries;
                }
            },
            closeObserver: {
                next: () => {
                    this.debugLog('Socket closed: {0}'.format(url));
                    this.socketState.next(WebsocketStreamConnectionState.Disconnected);
                    this.socketStateRaw = WebsocketStreamConnectionState.Disconnected;
                    this.reconnect(new Error(this.strings.Common.ConnectionRetiesError.message));
                }
            },
            // for compatibility with older version of rxjs websocket
            serializer: (value: string) => value
        });
        this.socket.subscribe({
            next: received => {
                const message = <WebsocketStreamPacket>received;
                this.debugLog('Socket received data.', message);
                if (message.state === WebsocketStreamState.Data) {
                    const handler = this.handlers.get(message.streamName);
                    if (handler) {
                        handler.process(message.data);
                    } else {
                        throw new Error(this.strings.Common.HandlerRegistrationError.message.format(message.streamName));
                    }
                } else if (message.state === WebsocketStreamState.Error) {
                    let errorMessage = this.strings.Common.CommunicationError.message;
                    if (message.data && message.data.error && message.data.error.message) {
                        errorMessage = this.strings.Common.CommunicationErrorDetail.message.format(message.data.error.message);
                    }

                    Logging.log({ level: LogLevel.Error, source: WebsocketStream.logSourceName, message: errorMessage });
                    this.reconnect(new Error(errorMessage));
                }
            },
            error: error => this.reconnect(error)
        });
    }

    private dispose(): void {
        if (this.socket) {
            this.socket.unsubscribe();
            this.socket = null;
        }
    }

    private reconnect(error: any): void {
        if (this.connectionRetries-- > 0) {
            this.dispose();
            setTimeout(() => this.initialize(false), WebsocketStream.reconnectWaitTime);
        } else {
            this.socketState.next(WebsocketStreamConnectionState.Failed);
            throw error;
        }
    }

    private debugLog(message: string, object?: any) {
        Logging.log({ level: LogLevel.Debug, source: WebsocketStream.logSourceName, message: message });
        if (object) {
            Logging.debug(object);
        }
    }
}
